/**
 * Capabilities are what a control on screen asks for; permissions are what the server
 * grants a company's role. They are not the same vocabulary and should not be: a screen
 * asks "may this person see pay on the schedule", the server answers "this role has
 * pay.view". `GRANTED_BY` below is the whole translation, one line per capability, and it
 * is derived from what the server actually enforces rather than from a wish.
 *
 * Nothing here decides anything. The server is the authority; this is so a screen offers
 * what the server will accept, and it is read from GET /tenant, per company, on every
 * load. A capability that no permission grants is marked so, and is never offered as a
 * checkbox (docs/design/2026-09-16-custom-roles.md).
 */
import { useTenant } from '@/shell/tenant';

export const CAPS = [
  ['vaktplan.se', 'Se vaktplanen'],
  ['vaktplan.endre', 'Endre og publisere vakter'],
  ['vaktplan.kost', 'Se kostnad på vaktplanen'],
  ['timeliste.se', 'Se timelisten'],
  ['timeliste.rette', 'Rette stemplede timer'],
  ['timeliste.godkjenne', 'Godkjenne timer'],
  ['timeliste.laase', 'Lukke og åpne en lønnsmåned'],
  ['fravaer.se', 'Se fravær'],
  ['fravaer.type', 'Se fraværstypen'],
  ['fravaer.godkjenne', 'Godkjenne fravær'],
  ['fravaer.registrere', 'Registrere fravær for andre'],
  ['ansatte.se', 'Se ansatte, navn og team'],
  ['ansatte.kontakt', 'Se kontaktopplysninger'],
  ['ansatte.loenn', 'Se lønn og sats'],
  ['ansatte.pnr', 'Se fødselsnummer'],
  ['ansatte.dokumenter', 'Se dokumenter'],
  ['ansatte.endre', 'Endre ansatte'],
  ['tilgjengelighet.godkjenne', 'Godkjenne tilgjengelighet'],
  ['timerloenn.se', 'Se lønnsgrunnlag'],
  ['timerloenn.sende', 'Sende timer til lønn'],
  ['rapporter.se', 'Se rapporter'],
  ['rapporter.kost', 'Se rapporter med kostnad'],
  ['prosjekter.se', 'Se prosjekter'],
  ['prosjekter.endre', 'Endre prosjekter'],
  ['innstillinger.se', 'Se innstillinger'],
  ['innstillinger.endre', 'Endre innstillinger'],
  ['roller.tildele', 'Tildele roller'],
  ['assistent.bruke', 'Bruke assistenten'],
  ['egen.se', 'Se egne vakter og timer'],
  ['lagvakt.se', 'Se lagets publiserte vaktplan'],
] as const;

export type Cap = (typeof CAPS)[number][0];

/**
 * How each capability is granted.
 *
 *   a permission key   the company's role must carry it
 *   'baseline'         everybody with a membership has it; it is not a checkbox, because
 *                      a person's own working life is not a company's to take away
 *   'admin'            the company's protected administrator only, and no permission
 *                      grants it: role assignment, and private files, which are self or
 *                      administrator on the server and not opened by managing people
 *   'unsupported'      the feature is not built; never granted, never offered
 */
type Grant = string | 'baseline' | 'admin' | 'unsupported';
const GRANTED_BY: Record<Cap, Grant> = {
  'vaktplan.se': 'schedule.view',
  'vaktplan.endre': 'schedule.manage',
  'vaktplan.kost': 'pay.view',
  'timeliste.se': 'timesheet.view',
  'timeliste.rette': 'timesheet.edit',
  'timeliste.godkjenne': 'timesheet.approve',
  'timeliste.laase': 'admin', // Lukk måned and its reopening, the administrator's on the server (D23, 2026-10-02)
  'fravaer.se': 'absence.view',
  'fravaer.type': 'absence.view',
  'fravaer.godkjenne': 'requests.decide',
  'fravaer.registrere': 'absence.manage',
  'ansatte.se': 'baseline', // names and this company's teams: the schedule needs them
  // The work contact reaches every colleague since 2026-09-20, the same as the
  // names above it; the permission that used to gate it no longer exists.
  'ansatte.kontakt': 'baseline',
  'ansatte.loenn': 'pay.view',
  // the server sends and accepts the number for the Administrator only (admin/userController.js), M3
  'ansatte.pnr': 'admin',
  // Excluded from the pilot on 2026-09-16 because the per-person behaviour was known to
  // be wrong: every client named the folder "user", so all employees shared one and the
  // server refused each of them. That is what plan 0020 fixed, so the exclusion is over.
  // It answers to documents.manage now, the key that covers reading a colleague's file,
  // filing into it and publishing to the company (ADR 0020, decision 6).
  'ansatte.dokumenter': 'documents.manage',
  'ansatte.endre': 'people.manage',
  // issue #70 (2026-10-12): asked for only while the company requires approval of availability
  'tilgjengelighet.godkjenne': 'availability.approve',
  'timerloenn.se': 'pay.view',
  'timerloenn.sende': 'pay.export',
  'rapporter.se': 'pay.view',
  'rapporter.kost': 'pay.view',
  'prosjekter.se': 'timesheet.view', // hours per project, basic (2026-09-29): the same as the server's report
  'prosjekter.endre': 'unsupported',
  'innstillinger.se': 'settings.operational',
  'innstillinger.endre': 'settings.operational',
  'roller.tildele': 'admin',
  // Owner, 2026-09-25: any role given the permission, not only the administrator.
  'assistent.bruke': 'ai.use',
  'egen.se': 'baseline',
  'lagvakt.se': 'baseline',
};

/** The capabilities a person with these permissions holds in this company. */
export function capsFrom(permissions: readonly string[] | undefined, isAdmin: boolean): Cap[] {
  const have = permissions ?? [];
  return CAPS.map((c) => c[0]).filter((cap) => {
    const g = GRANTED_BY[cap];
    if (g === 'unsupported') return false;
    if (g === 'baseline') return true;
    if (g === 'admin') return isAdmin;
    return isAdmin || have.includes(g);
  });
}

/**
 * Hook form: re-renders when the company or its answer changes, which is what makes a
 * permission taken away upstairs disappear from the screen on the next load rather than
 * at the next login. Before the company is verified nothing is granted but the baseline,
 * so anything that would send somebody away on a refusal waits for `ready` first.
 */
export function useAccess(): { ready: boolean; can: (cap: Cap) => boolean } {
  const { me, tenantStatus } = useTenant();
  const caps = capsFrom(me?.permissions, me?.is_admin === true);
  return { ready: tenantStatus !== 'pending', can: (cap) => caps.includes(cap) };
}

/** The common case: what may this person do here. */
export function useCan(): (cap: Cap) => boolean {
  return useAccess().can;
}
